import { Routes, Route, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import Footer from "./components/layout/Footer";
import MainNavigation from "./components/layout/MainNavigation";
import RouteAccessGuard from "./components/auth/RouteAccessGuard";
import ProductsCatalogPage from "./pages/ProductsCatalogPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CreateProductPage from "./pages/CreateProductPage";
import ManageAssetsPage from "./pages/ManageAssetsPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import MySubscriptionsPage from "./pages/MySubscriptionsPage";
import AddProduct from "./components/AddProduct";
import AddApiSpec from "./components/AddApiSpec";
import AddFaq from "./components/AddFaq";
import AddCategory from "./components/AddCategory";
import AddLink from "./components/AddLink";
import AddDocument from "./components/AddDocument";
import AddAppKyc from "./components/AddAppKyc";
import AddGatewaySku from "./components/AddGatewaySku";
import AddSupportCase from "./components/AddSupportCase";
import SupportCasesPage from "./pages/SupportCasesPage";
import SupportCaseDetailPage from "./pages/SupportCaseDetailPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import AddSubscription from "./components/AddSubscription";
import SubscriptionRequestsPage from "./pages/SubscriptionRequestsPage";
import AgentDashboardPage from "./pages/AgentDashboardPage";
import ArchitectAgentPage from "./pages/ArchitectAgentPage";
import ArchitectIntakeAssistantPage from "./pages/ArchitectIntakeAssistantPage";
import ProductionSupportOperatorHubPage from "./pages/ProductionSupportOperatorHubPage";
import IntegrationSupportHubPage from "./pages/IntegrationSupportHubPage";
import DevopsWorkspaceLayout from "./components/devops/DevopsWorkspaceLayout";
import DevopsAgentHubPage from "./pages/DevopsAgentHubPage";
import EnvironmentsHubPage from "./pages/EnvironmentsHubPage";
import ApplicationsHubPage from "./pages/ApplicationsHubPage";
import ConfigurationsHubPage from "./pages/ConfigurationsHubPage";
import ReleasesHubPage from "./pages/ReleasesHubPage";
import EnvironmentRecordPage from "./pages/EnvironmentRecordPage";
import ApplicationRecordPage from "./pages/ApplicationRecordPage";
import ConfigurationRecordPage from "./pages/ConfigurationRecordPage";
import ReleaseRecordPage from "./pages/ReleaseRecordPage";
import ApiExplorerPage from "./pages/ApiExplorerPage";
import ApiExplorerDetailPage from "./pages/ApiExplorerDetailPage";
import KafkaEventExplorerPage from "./pages/KafkaEventExplorerPage";
import KafkaEventExplorerDetailPage from "./pages/KafkaEventExplorerDetailPage";
import ProductExplorerPage from "./pages/ProductExplorerPage";
import CategoryExplorerPage from "./pages/CategoryExplorerPage";
import CategoryExplorerDetailPage from "./pages/CategoryExplorerDetailPage";
import ProductManagerDashboardPage from "./pages/product-manager/ProductManagerDashboardPage";
import ApiHubPage from "./pages/product-manager/ApiHubPage";
import FaqHubPage from "./pages/product-manager/FaqHubPage";
import LinkHubPage from "./pages/product-manager/LinkHubPage";
import DocumentHubPage from "./pages/product-manager/DocumentHubPage";
import TenantKnowledgeSearchPage from "./pages/product-manager/TenantKnowledgeSearchPage";
import ArchitectureStandardsHubPage from "./pages/product-manager/ArchitectureStandardsHubPage";
import CategoryHubPage from "./pages/product-manager/CategoryHubPage";
import ProductHubPage from "./pages/product-manager/ProductHubPage";
import ProductManagementGroupHubPage from "./pages/product-manager/ProductManagementGroupHubPage";
import AppKycHubPage from "./pages/product-manager/AppKycHubPage";
import GatewaySkuHubPage from "./pages/product-manager/GatewaySkuHubPage";
import GatewayRouteHubPage from "./pages/product-manager/GatewayRouteHubPage";
import AddGatewayRoute from "./components/AddGatewayRoute";
import DeveloperGuideHubPage from "./pages/product-manager/DeveloperGuideHubPage";
import OperationalErrorHubPage from "./pages/product-manager/OperationalErrorHubPage";
import KafkaEventHubPage from "./pages/product-manager/KafkaEventHubPage";
import ApiPortfolioIntelligencePage from "./pages/product-manager/ApiPortfolioIntelligencePage";
import ProductManagerCatalogAssistantPage from "./pages/product-manager/ProductManagerCatalogAssistantPage";
import AddDeveloperGuide from "./components/AddDeveloperGuide";
import AddOperationalError from "./components/AddOperationalError";
import AddKafkaEventTopic from "./components/AddKafkaEventTopic";
import PmAddRouteWrapper from "./components/product-manager/PmAddRouteWrapper";
import ConnectorsDashboardPage from "./pages/connectors/ConnectorsDashboardPage";
import PlatformDashboardPage from "./pages/PlatformDashboardPage";
import AddConnectorPage from "./pages/connectors/AddConnectorPage";
import { PM_HUBS } from "./utils/pmHubRoutes";
import { TENANT_ID_TOKEN_KEY } from "./api/authToken";
import { getTenantId } from "./utils/jwtUtils";
import { useEffect, useState } from "react";
import { ActiveJourneyProvider } from "./contexts/ActiveJourneyContext";

function App() {
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const tenantIdFromToken = getTenantId();
    setTenantId(tenantIdFromToken);

    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === TENANT_ID_TOKEN_KEY ||
        e.key === "sami_api_studio_token"
      ) {
        const newTenantId = getTenantId();
        setTenantId(newTenantId);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [tenantId]);

  return (
    <ActiveJourneyProvider>
      <MainNavigation>
        <Routes>
          <Route element={<RouteAccessGuard />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/product-catalog" element={<ProductsCatalogPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/create-product" element={<CreateProductPage />} />
            <Route path="/manage-assets" element={<ManageAssetsPage />} />
            <Route path="/api-explorer" element={<ApiExplorerPage />} />
            <Route
              path="/api-explorer/:apiId"
              element={<ApiExplorerDetailPage />}
            />
            <Route path="/kafka-event-explorer" element={<KafkaEventExplorerPage />} />
            <Route
              path="/kafka-event-explorer/:topicId"
              element={<KafkaEventExplorerDetailPage />}
            />
            <Route path="/product-explorer" element={<ProductExplorerPage />} />
            <Route path="/category-explorer" element={<CategoryExplorerPage />} />
            <Route
              path="/category-explorer/:categoryId"
              element={<CategoryExplorerDetailPage />}
            />
            <Route path="/agent-dashboard" element={<AgentDashboardPage />} />
            <Route
              path="/agent-dashboard/architect"
              element={<ArchitectAgentPage />}
            />
            <Route
              path="/agent-dashboard/architect/intake"
              element={<ArchitectIntakeAssistantPage />}
            />
            <Route
              path="/agent-dashboard/production-support"
              element={<ProductionSupportOperatorHubPage />}
            />
            <Route
              path="/agent-dashboard/integration-support"
              element={<IntegrationSupportHubPage />}
            />
            <Route element={<DevopsWorkspaceLayout />}>
              <Route
                path="/agent-dashboard/devops"
                element={<DevopsAgentHubPage />}
              />
              <Route path="/devops" element={<Navigate to="/agent-dashboard/devops" replace />} />
              <Route path="/devops/environments/new" element={<EnvironmentRecordPage mode="create" />} />
              <Route path="/devops/environments/:uuid/edit" element={<EnvironmentRecordPage mode="edit" />} />
              <Route path="/devops/environments/:uuid" element={<EnvironmentRecordPage mode="view" />} />
              <Route path="/devops/environments" element={<EnvironmentsHubPage />} />
              <Route path="/devops/applications/new" element={<ApplicationRecordPage mode="create" />} />
              <Route path="/devops/applications/:uuid/edit" element={<ApplicationRecordPage mode="edit" />} />
              <Route path="/devops/applications/:uuid" element={<ApplicationRecordPage mode="view" />} />
              <Route path="/devops/applications" element={<ApplicationsHubPage />} />
              <Route path="/devops/configurations/new" element={<ConfigurationRecordPage mode="create" />} />
              <Route path="/devops/configurations/:uuid/edit" element={<ConfigurationRecordPage mode="edit" />} />
              <Route path="/devops/configurations/:uuid" element={<ConfigurationRecordPage mode="view" />} />
              <Route path="/devops/configurations" element={<ConfigurationsHubPage />} />
              <Route path="/devops/releases/new" element={<ReleaseRecordPage mode="create" />} />
              <Route path="/devops/releases/:uuid/edit" element={<ReleaseRecordPage mode="edit" />} />
              <Route path="/devops/releases/:uuid" element={<ReleaseRecordPage mode="view" />} />
              <Route path="/devops/releases" element={<ReleasesHubPage />} />
            </Route>
            <Route
              path="/agent-dashboard/catalog-assistant"
              element={<ProductManagerCatalogAssistantPage />}
            />
            <Route
              path="/product-manager-dashboard/catalog-assistant"
              element={<Navigate to="/agent-dashboard/catalog-assistant" replace />}
            />
            <Route
              path="/product-manager-dashboard"
              element={<ProductManagerDashboardPage />}
            />
            <Route
              path="/product-manager-dashboard/api-hub"
              element={<ApiHubPage />}
            />
            <Route
              path="/product-manager-dashboard/faq-hub"
              element={<FaqHubPage />}
            />
            <Route
              path="/product-manager-dashboard/link-hub"
              element={<LinkHubPage />}
            />
            <Route
              path="/product-manager-dashboard/document-hub"
              element={<DocumentHubPage />}
            />
            <Route
              path="/product-manager-dashboard/knowledge-search"
              element={<TenantKnowledgeSearchPage />}
            />
            <Route
              path="/product-manager-dashboard/architecture-standards-hub"
              element={<ArchitectureStandardsHubPage />}
            />
            <Route
              path="/product-manager-dashboard/category-hub"
              element={<CategoryHubPage />}
            />
            <Route
              path="/product-manager-dashboard/product-hub"
              element={<ProductHubPage />}
            />
            <Route
              path="/product-manager-dashboard/product-management-groups"
              element={<ProductManagementGroupHubPage />}
            />
            <Route
              path="/product-manager-dashboard/app-kyc-hub"
              element={<AppKycHubPage />}
            />
            <Route
              path="/product-manager-dashboard/gateway-sku-hub"
              element={<GatewaySkuHubPage />}
            />
            <Route
              path="/product-manager-dashboard/gateway-route-hub"
              element={<GatewayRouteHubPage />}
            />
            <Route
              path="/product-manager-dashboard/developer-guide-hub"
              element={<DeveloperGuideHubPage />}
            />
            <Route
              path="/product-manager-dashboard/operational-error-hub"
              element={<OperationalErrorHubPage />}
            />
            <Route
              path="/product-manager-dashboard/kafka-event-hub"
              element={<KafkaEventHubPage />}
            />
            <Route
              path="/product-manager-dashboard/api-portfolio-intelligence"
              element={<ApiPortfolioIntelligencePage />}
            />
            <Route
              path="/product-manager-dashboard/api-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.api}>
                  {({ returnPath, returnSearch }) => (
                    <AddApiSpec
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/faq-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.faq}>
                  {({ returnPath, returnSearch }) => (
                    <AddFaq
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/link-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.link}>
                  {({ returnPath, returnSearch }) => (
                    <AddLink
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/document-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.document}>
                  {({ returnPath, returnSearch }) => (
                    <AddDocument
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/category-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.category}>
                  {({ returnPath, returnSearch }) => (
                    <AddCategory
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/product-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.product}>
                  {({ returnPath, returnSearch }) => (
                    <AddProduct
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/app-kyc-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.appKyc}>
                  {({ returnPath, returnSearch }) => (
                    <AddAppKyc
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/gateway-sku-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.gatewaySku}>
                  {({ returnPath, returnSearch }) => (
                    <AddGatewaySku
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/gateway-route-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.gatewayRoute}>
                  {({ returnPath, returnSearch }) => (
                    <AddGatewayRoute
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/developer-guide-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.developerGuide}>
                  {({ returnPath, returnSearch }) => (
                    <AddDeveloperGuide
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/operational-error-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.operationalError}>
                  {({ returnPath, returnSearch }) => (
                    <AddOperationalError
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/product-manager-dashboard/kafka-event-hub/add"
              element={
                <PmAddRouteWrapper returnPath={PM_HUBS.kafkaEvent}>
                  {({ returnPath, returnSearch }) => (
                    <AddKafkaEventTopic
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/connectors-dashboard"
              element={<ConnectorsDashboardPage />}
            />
            <Route
              path="/connectors-dashboard/:providerSlug/add"
              element={<AddConnectorPage tenantId={tenantId} />}
            />
            <Route path="/platform-dashboard" element={<PlatformDashboardPage />} />
            <Route
              path="/platform-dashboard/:section"
              element={<PlatformDashboardPage />}
            />
            <Route
              path="/analytics-dashboard"
              element={<AnalyticsDashboardPage />}
            />
            <Route path="/support-cases" element={<SupportCasesPage />} />
            <Route
              path="/support-cases/add"
              element={
                <PmAddRouteWrapper returnPath="/support-cases">
                  {({ returnPath, returnSearch }) => (
                    <AddSupportCase
                      tenantId={tenantId}
                      returnPath={returnPath}
                      returnSearch={returnSearch}
                    />
                  )}
                </PmAddRouteWrapper>
              }
            />
            <Route
              path="/support-cases/:caseId"
              element={<SupportCaseDetailPage />}
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/org-admin" element={<Navigate to="/settings" replace />} />
            <Route path="/my-subscriptions" element={<MySubscriptionsPage />} />
            <Route path="/add-subscription" element={<AddSubscription />} />
            <Route
              path="/subscription-requests"
              element={<SubscriptionRequestsPage />}
            />
            <Route
              path="/manage-assets/products/add"
              element={<AddProduct tenantId={tenantId} />}
            />
            <Route
              path="/manage-assets/apis/add"
              element={<AddApiSpec tenantId={tenantId} />}
            />
            <Route
              path="/manage-assets/faqs/add"
              element={<AddFaq tenantId={tenantId} />}
            />
            <Route
              path="/manage-assets/categories/add"
              element={<AddCategory tenantId={tenantId} />}
            />
            <Route
              path="/manage-assets/links/add"
              element={<AddLink tenantId={tenantId} />}
            />
            <Route
              path="/manage-assets/documents/add"
              element={<AddDocument />}
            />
          </Route>
        </Routes>
        <Footer />
      </MainNavigation>
    </ActiveJourneyProvider>
  );
}

export default App;
